/**
 * EcoSealPro Merged Variant Picker Manager
 * Coordinates between carousel and custom variant pickers
 */

(function() {
  'use strict';
  
  /**
   * In quick-view, the same picker exists twice (page + quick-view). If we query via `document`,
   * we often hit the wrong instance. Scope everything to the currently open quick-view if present.
   */
  const getScopeRoot = function() {
    return (
      // Most reliable: the opened quick-view drawer content lives inside <quick-view-drawer><details open>…</details>
      document.querySelector('quick-view-drawer details[open]') ||
      // During the opening animation, some themes use a transition class before the final open class
      document.querySelector('quick-view-drawer details.menu-opening') ||
      // Fallbacks (some themes place state classes on <body>)
      document.querySelector('.quick-view--open') ||
      document.querySelector('.quick-view--opening') ||
      document
    );
  };

  const getMergedContainer = function(root) {
    const scope = root || getScopeRoot();
    return scope.querySelector('.ecoseal-merged-picker');
  };

  // Global merged picker controller
  window.ecosealMergedPicker = {
    // Per-container init guards (quick-view content is injected dynamically)
    _initialisedContainers: null,
    _carouselAssetsPromise: null,
    _pendingCustomInitTimer: null,
    _pendingCustomInitTries: 0,
    
    /**
     * Initialize the merged picker system
     */
    init: function() {      
      const root = getScopeRoot();
      const container = getMergedContainer(root);
      if (!container) return;
      const ctx = { root, container };

      // Guard per rendered picker instance (prevents duplicate listeners when init() is called repeatedly)
      if (!this._initialisedContainers) {
        this._initialisedContainers = new WeakSet();
      }
      if (this._initialisedContainers.has(container)) {
        return;
      }
      this._initialisedContainers.add(container);

      // Intelligent initialization based on current variant
      this.intelligentInit(ctx);
      
      // Setup switching functionality
      this.setupSwitching(ctx);
    },

    /**
     * Get the active DOM scope + merged picker container for the currently open quick-view (or page).
     */
    getContext: function() {
      const root = getScopeRoot();
      const container = getMergedContainer(root);
      return { root, container };
    },
    
    /**
     * Intelligent initialization that determines what to show based on current variant
     */
    intelligentInit: function(ctx) {
      const context = ctx || this.getContext();
      // Get current variant info
      const currentVariant = this.getCurrentVariant(context);
      
      if (!currentVariant) {
        // Default to the custom picker (it is the visible default and avoids loading carousel assets).
        this.initializeCustom(context);
        this.showCustomizer(context);
        return;
      }      
      // Check if current variant is a fixed set
      const matchingFixedSet = this.findFixedSetByVariant(currentVariant);
      
      if (matchingFixedSet) {
        // Carousel mode (load carousel assets only if needed)
        this.showCarousel(context);
        if (!window.EcoSealCarouselPicker) {
          this.ensureCarouselAssetsLoaded()
            .then(() => {
              // Async: re-resolve context (quick-view can change between ticks)
              const ctxNow = this.getContext();
              this.initializeCarousel(ctxNow);
              this.showCarouselWithSet(matchingFixedSet, ctxNow);
            })
            .catch(() => {
              // If carousel fails to load for any reason, fall back to the custom picker.
              const ctxNow = this.getContext();
              this.initializeCustom(ctxNow);
              this.showCustomizer(ctxNow);
            });
          return;
        }
        this.initializeCarousel(context);
        this.showCarouselWithSet(matchingFixedSet, context);
      } else {
        // Default path: custom picker is visible, so only initialize custom.
        this.initializeCustom(context);
        this.showCustomizer(context);
      }
    },
    
    /**
     * Get the currently selected variant
     * @return {Object|null} - Current variant object or null
     */
    getCurrentVariant: function(ctx) {
      const root = (ctx && ctx.root) ? ctx.root : this.getContext().root;
      const scope = root || document;

      // Prefer URL param first
      const urlVariantId = this.getUrlParameter('variant');
      if (urlVariantId) {
        // If DOM has a matching option by data-variant-id, prefer that
        const optByUrl = scope.querySelector(`select[id^="Option-"][name^="options["] option[data-variant-id="${urlVariantId}"]`);
        if (optByUrl) {
          return { id: Number(urlVariantId), sku: optByUrl.dataset.variantSku || null };
        }
      }

      // Get currently selected variant from DOM data attributes
      const selectElement = scope.querySelector('select[id^="Option-"][name^="options["]');
      if (selectElement && selectElement.selectedIndex >= 0) {
        const currentSelectedOption = selectElement.options[selectElement.selectedIndex];
        if (currentSelectedOption) {
          const idAttr = currentSelectedOption.dataset.variantId;
          const skuAttr = currentSelectedOption.dataset.variantSku || null;
          if (idAttr) {
            return { id: Number(idAttr), sku: skuAttr };
          }
        }
      }

      // Fallback to hidden input name="id"
      const idInput = scope.querySelector('input[name="id"]');
      if (idInput && idInput.value) {
        return { id: Number(idInput.value) };
      }

      return null;
    },
    
    /**
     * Find fixed set by variant
     * @param {Object} variant - Variant object
     * @return {Object|null} - Matching fixed set or null
     */
    findFixedSetByVariant: function(variant) {
      if (!variant || !variant.sku) return null;
      
      // Fast path: if Liquid provided fixed-set SKUs, use that (no need to download carousel JS).
      if (Array.isArray(window.ecosealFixedSetSkus) && window.ecosealFixedSetSkus.indexOf(variant.sku) !== -1) {
        return { sku: variant.sku };
      }

      // Fallback: if carousel is already loaded, use its config.
      if (window.EcoSealCarouselPicker && window.EcoSealCarouselPicker.Config && window.EcoSealCarouselPicker.Config.fixedSets) {
        return window.EcoSealCarouselPicker.Config.fixedSets.find(set => set.sku === variant.sku);
      }
      
      return null;
    },
    
    /**
     * Show carousel with specific set preselected
     * @param {Object} fixedSet - Fixed set to preselect
     */
    showCarouselWithSet: function(fixedSet, ctx) {      
      // Show carousel interface
      this.showCarousel(ctx);
      
      // Small delay to ensure carousel is ready, then preselect the specific set
      setTimeout(() => {
        if (window.EcoSealCarouselPicker && typeof window.EcoSealCarouselPicker.selectSetBySku === 'function') {
          // Use SKU-based selection to avoid variant update
          window.EcoSealCarouselPicker.selectSetBySku(fixedSet.sku, true); // true = skip variant update
        } else if (window.EcoSealCarouselPicker && typeof window.EcoSealCarouselPicker.selectSet === 'function' && fixedSet.id) {
          // Fallback to set ID selection
          window.EcoSealCarouselPicker.selectSet(fixedSet, true); // true = skip variant update
        }
      }, 100);
    },
    
    /**
     * Show customizer interface
     */
    showCustomizer: function(ctx) {      
      const container = (ctx && ctx.container) ? ctx.container : this.getContext().container;
      if (!container) return;

      const carouselElement = container.querySelector('#carousel-picker');
      const customElement = container.querySelector('#custom-picker');
      
      if (!carouselElement || !customElement) {
        console.error('[MergedPicker] Required elements not found');
        return;
      }
      
      // Hide carousel
      carouselElement.style.display = 'none';
      
      // Show custom picker
      customElement.style.display = 'block';
      
      // Do NOT reset the custom picker - let it initialize from current variant
    },
    
    /**
     * Show carousel interface
     */
    showCarousel: function(ctx) {      
      const container = (ctx && ctx.container) ? ctx.container : this.getContext().container;
      if (!container) return;

      const carouselElement = container.querySelector('#carousel-picker');
      const customElement = container.querySelector('#custom-picker');
      
      if (!carouselElement || !customElement) {
        console.error('[MergedPicker] Required elements not found');
        return;
      }
      
      // Hide custom picker
      customElement.style.display = 'none';
      
      // Show carousel
      carouselElement.style.display = 'block';
    },
    
    /**
     * Get URL parameter value
     * @param {string} name - Parameter name
     * @return {string} - Parameter value or empty string
     */
    getUrlParameter: function(name) {
      name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
      const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
      const results = regex.exec(location.search);
      return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    },
    
    /**
     * Initialize the carousel picker
     */
    initializeCarousel: function(ctx) {
      const container = (ctx && ctx.container) ? ctx.container : this.getContext().container;
      if (!container) return;
      const carouselElement = container.querySelector('#carousel-picker');
      if (carouselElement && carouselElement.dataset.ecosealMergedCarouselInit === 'true') {
        return;
      }

      // If the carousel script wasn't loaded yet, load it lazily and retry.
      if (!window.EcoSealCarouselPicker) {
        this.ensureCarouselAssetsLoaded()
          .then(() => this.initializeCarousel())
          .catch(() => {});
        return;
      }
            
      // Load jar image URLs from Shopify if available for carousel
      if (window.fixedSetsAssets && window.fixedSetsAssets.setImages) {
        // Carousel assets are handled in its own config
      }
      
      // Initialize carousel if the global object exists
      const hasSets = !!(window.EcoSealCarouselPicker
        && window.EcoSealCarouselPicker.Config
        && Array.isArray(window.EcoSealCarouselPicker.Config.fixedSets)
        && window.EcoSealCarouselPicker.Config.fixedSets.length > 0);

      if (window.EcoSealCarouselPicker && typeof window.EcoSealCarouselPicker.init === 'function' && hasSets) {
        window.EcoSealCarouselPicker.init();
        if (carouselElement) {
          carouselElement.dataset.ecosealMergedCarouselInit = 'true';
        }
      } else {
        // If no sets, hide carousel container gracefully if present
        if (carouselElement) {
          carouselElement.style.display = 'none';
        }
      }
    },
    
    /**
     * Initialize the custom variant picker
     */
    initializeCustom: function(ctx) {
      const container = (ctx && ctx.container) ? ctx.container : this.getContext().container;
      if (!container) return;
      const customElement = container.querySelector('#custom-picker');
      if (customElement && customElement.dataset.ecosealMergedCustomInit === 'true') {
        return;
      }
      
      // Initialize custom picker if the global object exists
      if (window.EcoSealPicker && typeof window.EcoSealPicker.init === 'function') {
        window.EcoSealPicker.init();
        if (customElement) {
          customElement.dataset.ecosealMergedCustomInit = 'true';
        }
        // Reset retry state after a successful init
        this._pendingCustomInitTries = 0;
        if (this._pendingCustomInitTimer) {
          clearTimeout(this._pendingCustomInitTimer);
          this._pendingCustomInitTimer = null;
        }
      } else {
        // In quick-view, scripts are re-injected and `defer` order isn't guaranteed.
        // If merged picker runs before ecoseal-variant-picker.js finished loading, retry briefly.
        if (this._pendingCustomInitTimer) return;
        if (this._pendingCustomInitTries >= 40) {
          console.warn('[MergedPicker] EcoSealPicker not found (timed out)');
          return;
        }
        this._pendingCustomInitTries += 1;
        this._pendingCustomInitTimer = setTimeout(() => {
          this._pendingCustomInitTimer = null;
          this.initializeCustom();
        }, 50);
      }
    },
    
    /**
     * Switch to custom picker view
     */
    switchToCustom: function() {
      const ctx = this.getContext();
      // Ensure the custom picker is initialised before showing it.
      this.initializeCustom(ctx);
      
      const { container } = ctx;
      if (!container) return;

      const carouselElement = container.querySelector('#carousel-picker');
      const customElement = container.querySelector('#custom-picker');
      
      if (!carouselElement || !customElement) {
        console.error('[MergedPicker] Required elements not found');
        return;
      }
      
      // Hide carousel
      carouselElement.style.display = 'none';
      
      // Show custom picker
      customElement.style.display = 'block';
      
      // Instead of resetting, simply refresh the UI so the previously configured
      // custom selection (if any) is shown again and the variant is updated accordingly
      if (window.EcoSealPicker && typeof window.EcoSealPicker.updateUI === 'function') {
        window.EcoSealPicker.updateUI(true); // true ⇒ also update variant to match current selection
      }
    },
    
    /**
     * Switch to carousel picker view
     */
    switchToCarousel: function() {      
      const ctx = this.getContext();
      // Ensure carousel assets are available before we switch.
      if (!window.EcoSealCarouselPicker) {
        this.showCarousel(ctx);
        this.ensureCarouselAssetsLoaded()
          .then(() => {
            // Async: re-resolve context (quick-view can change between ticks)
            const ctxNow = this.getContext();
            this.initializeCarousel(ctxNow);
            // If there are no fixed sets configured, stay in custom mode
            const hasSets = !!(window.EcoSealCarouselPicker
              && window.EcoSealCarouselPicker.Config
              && Array.isArray(window.EcoSealCarouselPicker.Config.fixedSets)
              && window.EcoSealCarouselPicker.Config.fixedSets.length > 0);
            if (!hasSets) {
              this.switchToCustom();
              return;
            }
            this.resetCarouselToDefault();
          })
          .catch(() => this.switchToCustom());
        return;
      }

      const { container } = ctx;
      if (!container) return;

      const carouselElement = container.querySelector('#carousel-picker');
      const customElement = container.querySelector('#custom-picker');
      
      if (!carouselElement || !customElement) {
        console.error('[MergedPicker] Required elements not found');
        return;
      }
      
      // If there are no fixed sets configured, stay in custom mode
      const hasSets = !!(window.EcoSealCarouselPicker
        && window.EcoSealCarouselPicker.Config
        && Array.isArray(window.EcoSealCarouselPicker.Config.fixedSets)
        && window.EcoSealCarouselPicker.Config.fixedSets.length > 0);
      if (!hasSets) {
        this.switchToCustom();
        return;
      }

      // Toggle to carousel view
      this.showCarousel(ctx);
      
      // Show default set in carousel and update variant
      this.resetCarouselToDefault();
    },

    /**
     * Lazy-load carousel CSS+JS (used only when a fixed set is active or user switches to carousel).
     * @return {Promise<void>}
     */
    ensureCarouselAssetsLoaded: function() {
      if (this._carouselAssetsPromise) return this._carouselAssetsPromise;
      if (window.EcoSealCarouselPicker) {
        this._carouselAssetsPromise = Promise.resolve();
        return this._carouselAssetsPromise;
      }

      const assets = window.ecosealCarouselAssets || {};
      const jsUrl = assets.js;
      const cssUrl = assets.css;

      this._carouselAssetsPromise = new Promise((resolve, reject) => {
        if (!jsUrl) {
          reject(new Error('Missing ecosealCarouselAssets.js'));
          return;
        }

        // Inject stylesheet once
        if (cssUrl) {
          const existingCss = document.querySelector(`link[rel="stylesheet"][href="${cssUrl}"]`);
          if (!existingCss) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = cssUrl;
            document.head.appendChild(link);
          }
        }

        // If a script tag already exists, hook into it
        const existingScript = document.querySelector(`script[src="${jsUrl}"]`);
        if (existingScript) {
          if (window.EcoSealCarouselPicker) {
            resolve();
            return;
          }
          existingScript.addEventListener('load', () => window.EcoSealCarouselPicker ? resolve() : reject(new Error('Carousel script loaded but global missing')));
          existingScript.addEventListener('error', () => reject(new Error('Carousel script failed to load')));
          return;
        }

        const script = document.createElement('script');
        script.src = jsUrl;
        script.defer = true;
        script.onload = () => window.EcoSealCarouselPicker ? resolve() : reject(new Error('Carousel script loaded but global missing'));
        script.onerror = () => reject(new Error('Carousel script failed to load'));
        document.head.appendChild(script);
      });

      return this._carouselAssetsPromise;
    },
    
    /**
     * Reset carousel to default set (Meal-Prep) and update variant
     */
    resetCarouselToDefault: function() {
      if (window.EcoSealCarouselPicker && typeof window.EcoSealCarouselPicker.getDefaultSet === 'function') {
        const defaultSet = window.EcoSealCarouselPicker.getDefaultSet();
        if (defaultSet && typeof window.EcoSealCarouselPicker.selectSet === 'function') {
          window.EcoSealCarouselPicker.selectSet(defaultSet, false); // Update variant
        }
      }
    },
    
    /**
     * Setup switching functionality
     */
    setupSwitching: function(ctx) {
      const container = (ctx && ctx.container) ? ctx.container : this.getContext().container;
      if (!container) return;

      // Back button functionality
      const showCarouselLink = container.querySelector('#show-carousel-link');
      if (showCarouselLink) {
        showCarouselLink.addEventListener('click', (e) => {
          e.preventDefault();
          this.switchToCarousel();
        });
      } else {
        console.warn('[MergedPicker] "Show carousel" link not found');
      }

      const backToCustomButton = container.querySelector('#back-to-custom');
      if (backToCustomButton) {
        backToCustomButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.switchToCustom();
        });
      } else {
        console.warn('[MergedPicker] "Back to custom" button not found');
      }

      // Custom set card click handling is done in carousel JS
      // The click handler calls window.ecosealMergedPicker.switchToCustom()
    }
  };
  
  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    // Only initialize if merged picker container exists
    if (getMergedContainer(getScopeRoot())) {
      window.ecosealMergedPicker.init();
    }
  });
  
  // Fallback initialization for dynamic content
  (function() {
    const initializeMergedPicker = function() {
      if (!getMergedContainer(getScopeRoot())) return;
      if (window.ecosealMergedPicker && typeof window.ecosealMergedPicker.init === 'function') {
        window.ecosealMergedPicker.init();
      }
    };

    if (document.readyState !== 'loading') {
      initializeMergedPicker();
    }

    document.addEventListener('quickview:loaded', initializeMergedPicker);
  })();
  
})(); 